import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue, set, get } from 'firebase/database';
import { LogIn } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const CANVAS_W      = 800;
const CANVAS_H      = 300;
const GROUND_Y      = 236;
const PLAYER_X      = 64;
const PLAYER_SIZE   = 56;
const GRAVITY       = 2200;   // px/s^2
const JUMP_VELOCITY = -760;   // px/s
const START_SPEED   = 320;    // px/s
const MAX_SPEED     = 680;    // px/s
const SPEED_RAMP    = 6;      // px/s gained per second survived
const HIGH_SCORE_KEY = 'pokeDashHighScore';
const PLAYER_SPRITE  = '/sprite.png'; // Togepi

export default function PokeDash({ database, discordUser, onLogin }) {
  const canvasRef = useRef(null);
  const spriteRef = useRef(null);
  const spriteLoadedRef = useRef(false);
  const rafRef = useRef(null);
  const stateRef = useRef(null);
  const [gameState, setGameState] = useState('idle'); // idle | playing | over
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const myUid = discordUser?.id;

  useEffect(() => {
    if (!database) return;
    const lbRef = ref(database, 'pokeDashLeaderboard');
    const unsub = onValue(lbRef, (snap) => {
      const data = snap.val() || {};
      const list = Object.values(data)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
      setLeaderboard(list);
    });
    return () => unsub();
  }, [database]);

  useEffect(() => {
    const saved = parseInt(localStorage.getItem(HIGH_SCORE_KEY), 10);
    if (!isNaN(saved)) setHighScore(saved);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = PLAYER_SPRITE;
    img.onload = () => { spriteLoadedRef.current = true; };
    spriteRef.current = img;
  }, []);

  const resetState = useCallback(() => {
    stateRef.current = {
      playerY: GROUND_Y - PLAYER_SIZE,
      velocityY: 0,
      onGround: true,
      obstacles: [],
      speed: START_SPEED,
      distance: 0,
      timeSinceSpawn: 0,
      nextSpawnIn: 1.1,
      lastTime: null,
    };
  }, []);

  const jump = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    if (s.onGround) {
      s.velocityY = JUMP_VELOCITY;
      s.onGround = false;
    }
  }, []);

  const startGame = useCallback(() => {
    resetState();
    setScore(0);
    setGameState('playing');
  }, [resetState]);

  const draw = useCallback((ctx) => {
    const s = stateRef.current;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    const bg = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    bg.addColorStop(0, '#0a0a0d');
    bg.addColorStop(1, '#050506');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Ground line
    const groundGrad = ctx.createLinearGradient(0, 0, CANVAS_W, 0);
    groundGrad.addColorStop(0, '#04aff7');
    groundGrad.addColorStop(1, '#f42ae4');
    ctx.fillStyle = groundGrad;
    ctx.fillRect(0, GROUND_Y, CANVAS_W, 2);
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.fillRect(0, GROUND_Y + 2, CANVAS_W, CANVAS_H - GROUND_Y - 2);

    if (!s) return;

    // Player
    const py = s.playerY;
    if (spriteLoadedRef.current && spriteRef.current) {
      ctx.drawImage(spriteRef.current, PLAYER_X, py, PLAYER_SIZE, PLAYER_SIZE);
    } else {
      ctx.fillStyle = '#FCD34D';
      ctx.beginPath();
      ctx.arc(PLAYER_X + PLAYER_SIZE / 2, py + PLAYER_SIZE / 2, PLAYER_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Obstacles (pokeballs) — rotated to look like they're rolling
    s.obstacles.forEach((o) => {
      const cx = o.x + o.size / 2;
      const cy = GROUND_Y - o.size / 2;
      const r = o.size / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(o.rot || 0);
      ctx.beginPath();
      ctx.arc(0, 0, r, Math.PI, 0);
      ctx.fillStyle = '#ee1515';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.lineWidth = Math.max(1.5, r * 0.14);
      ctx.strokeStyle = '#18181b';
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-r, 0);
      ctx.lineTo(r, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.32, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = Math.max(1.2, r * 0.1);
      ctx.stroke();
      ctx.restore();
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const tick = (now) => {
      const s = stateRef.current;
      if (gameState === 'playing' && s) {
        if (s.lastTime == null) s.lastTime = now;
        const dt = Math.min((now - s.lastTime) / 1000, 0.05);
        s.lastTime = now;

        s.speed = Math.min(MAX_SPEED, s.speed + SPEED_RAMP * dt);
        s.distance += s.speed * dt;

        // Player physics
        s.velocityY += GRAVITY * dt;
        s.playerY += s.velocityY * dt;
        const groundLevel = GROUND_Y - PLAYER_SIZE;
        if (s.playerY >= groundLevel) {
          s.playerY = groundLevel;
          s.velocityY = 0;
          s.onGround = true;
        }

        // Spawn obstacles
        s.timeSinceSpawn += dt;
        if (s.timeSinceSpawn >= s.nextSpawnIn) {
          s.timeSinceSpawn = 0;
          const minGap = 0.75 + Math.random() * 0.6;
          s.nextSpawnIn = minGap * (START_SPEED / s.speed) + Math.random() * 0.3;
          const size = 30 + Math.random() * 16;
          s.obstacles.push({ x: CANVAS_W + size, size });
        }

        // Move obstacles + collision
        const playerBox = {
          x: PLAYER_X + PLAYER_SIZE * 0.22,
          y: s.playerY + PLAYER_SIZE * 0.18,
          w: PLAYER_SIZE * 0.56,
          h: PLAYER_SIZE * 0.72,
        };
        let hit = false;
        s.obstacles.forEach((o) => {
          const dx = s.speed * dt;
          o.x -= dx;
          o.rot = (o.rot || 0) - dx / (o.size / 2);
        });
        s.obstacles = s.obstacles.filter((o) => o.x + o.size > -10);
        s.obstacles.forEach((o) => {
          const ob = { x: o.x + o.size * 0.12, y: GROUND_Y - o.size * 0.92, w: o.size * 0.76, h: o.size * 0.9 };
          if (
            playerBox.x < ob.x + ob.w &&
            playerBox.x + playerBox.w > ob.x &&
            playerBox.y < ob.y + ob.h &&
            playerBox.y + playerBox.h > ob.y
          ) hit = true;
        });

        const newScore = Math.floor(s.distance / 10);
        setScore((prev) => (prev !== newScore ? newScore : prev));

        if (hit) {
          setGameState('over');
          setHighScore((prevHigh) => {
            if (newScore > prevHigh) {
              localStorage.setItem(HIGH_SCORE_KEY, String(newScore));
              return newScore;
            }
            return prevHigh;
          });
          if (database && myUid && newScore > 0) {
            const entryRef = ref(database, `pokeDashLeaderboard/${myUid}`);
            get(entryRef).then((snap) => {
              const existing = snap.val();
              if (!existing || newScore > existing.score) {
                set(entryRef, {
                  uid: myUid,
                  username: discordUser.nickname || discordUser.username,
                  avatar: discordUser.avatar ? `https://cdn.discordapp.com/avatars/${myUid}/${discordUser.avatar}.png` : null,
                  score: newScore,
                });
              }
            });
          }
        }
      } else if (s) {
        s.lastTime = null;
      }

      draw(ctx);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [gameState, draw, discordUser, database, myUid]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        if (gameState === 'idle') startGame();
        else if (gameState === 'playing') jump();
        else if (gameState === 'over') startGame();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [gameState, startGame, jump]);

  const handlePointerDown = () => {
    if (gameState === 'idle') startGame();
    else if (gameState === 'playing') jump();
    else if (gameState === 'over') startGame();
  };

  // ─── RENDER: Not logged in ─────────────────────────────────────────────────
  if (!discordUser) {
    return (
      <div className="relative flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div
          className="fixed left-0 right-0 bottom-0 pointer-events-none"
          style={{ top: '100px', background: 'linear-gradient(160deg, rgba(4,175,247,0.08), rgba(0,0,0,0.4))' }}
        />
        <img src="/pball.svg" alt="" className="relative z-10 h-12 w-12 animate-spin" style={{ animationDuration: '2s', filter: 'brightness(0) invert(1)' }} />
        <div className="relative z-10">
          <h2 className="text-white font-bold text-xl">Cheeky Dash</h2>
          <p className="text-white text-sm mt-1">Get Togepi past the obstacles — how far can you get?</p>
        </div>
        <div className="relative z-10 flex flex-col gap-1 rounded-xl px-4 py-3 border border-zinc-800/40 backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <p className="text-white text-sm">You need to log in with Discord to play!</p>
          <button
            onClick={onLogin}
            className="flex items-center justify-center gap-1.5 text-white font-semibold text-xs rounded-lg px-4 py-2 transition-all duration-200 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)' }}
          >
            <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
            Log in with Discord
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: Logged in — the game ──────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col items-center px-4 py-6 gap-3">
      {leaderboard.length > 0 && (
        <div className="w-full rounded-xl border border-zinc-800/60 corner-glow bg-gradient-to-br from-zinc-800/85 to-black/90 px-3 py-2.5" style={{ maxWidth: CANVAS_W }}>
          <style>{`
            @keyframes trophyBounce { 0%, 100% { transform: translateY(0) rotate(0deg); } 25% { transform: translateY(-2px) rotate(-8deg); } 50% { transform: translateY(0) rotate(0deg); } 75% { transform: translateY(-2px) rotate(8deg); } }
            .trophy-bounce { display: inline-block; animation: trophyBounce 1.4s ease-in-out infinite; }
          `}</style>
          <div className="flex items-center gap-1.5">
            <span className="trophy-bounce" style={{ fontSize: '12px' }}>🏆</span>
            <p className="text-white text-[10px] font-bold uppercase tracking-wider">Leaderboard</p>
            <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">Top {leaderboard.length}</p>
          </div>
          <div className="flex flex-col gap-1 mt-1.5">
            {leaderboard.map((entry, i) => (
              <div
                key={entry.uid}
                className="flex items-center gap-2"
                style={entry.uid === myUid ? { background: 'rgba(4,175,247,0.12)', borderRadius: '6px', padding: '2px 4px', margin: '-2px -4px' } : undefined}
              >
                <span className="text-zinc-400 font-mono text-[10px] w-4 flex-shrink-0 text-center">{i + 1}</span>
                <span className="text-white text-[10px] font-medium flex-1 truncate">{entry.username}</span>
                <span className="text-white text-[10px] font-semibold font-mono flex-shrink-0">{entry.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center gap-3 w-full">
      <div className="flex items-center justify-between w-full" style={{ maxWidth: CANVAS_W }}>
        <h2 className="text-white font-bold text-base">Cheeky Dash</h2>
        <div className="flex items-center gap-3 text-xs font-mono">
          <span className="text-zinc-400">SCORE <span className="text-white font-semibold">{score}</span></span>
        </div>
      </div>

      <div
        className="relative w-full rounded-xl overflow-hidden border border-zinc-800/60 corner-glow"
        style={{ maxWidth: CANVAS_W, aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, cursor: 'pointer' }}
        onMouseDown={handlePointerDown}
        onTouchStart={(e) => { e.preventDefault(); handlePointerDown(); }}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="w-full h-full block"
          style={{ imageRendering: 'pixelated' }}
        />

        {gameState !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none" style={{ background: 'rgba(0,0,0,0.45)' }}>
            {gameState === 'idle' && (
              <>
                <p className="text-white font-bold text-lg">Ready?</p>
                <p className="text-zinc-300 text-xs">Tap, click, or press Space to jump</p>
              </>
            )}
            {gameState === 'over' && (
              <>
                <p className="text-white font-bold text-lg">Game Over</p>
                <p className="text-zinc-300 text-xs">Score: {score}{score >= highScore && score > 0 ? ' — New High Score!' : ''}</p>
                <p className="text-zinc-400 text-[11px] mt-1">Tap, click, or press Space to try again</p>
              </>
            )}
          </div>
        )}
      </div>

      <button
        onMouseDown={handlePointerDown}
        onTouchStart={(e) => { e.preventDefault(); handlePointerDown(); }}
        className="flex items-center justify-center font-black text-white text-sm uppercase tracking-wide active:scale-90 transition-transform duration-100 select-none mt-6 sm:mt-0"
        style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          boxShadow: '0 6px 18px rgba(239,68,68,0.45)',
        }}
      >
        Jump
      </button>

      <p className="text-zinc-500 text-[10px]">Get past the obstacles — don't get caught!</p>
      </div>
    </div>
  );
}
